import { Label } from "./ui/label";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { LOSS_REASONS, INTERACTION_TYPES } from "../lib/pipeline";

// Stages that require a logged Interaction (why the deal moved) before the
// status change is accepted (mirrors backend/prospects/models.py
// INTERACTION_REQUIRED_STAGES). "meeting_scheduled" and "lost" have their own
// dedicated fields below instead (a scheduled Task, and loss_reason).
const INTERACTION_REQUIRED_STAGES = ["new_lead", "contacted", "qualified", "demo_negotiation", "won"];

export function defaultStatusExtra(status) {
  if (status === "lost") return { loss_reason: "Prix" };
  if (status === "meeting_scheduled") return { due_date: "", description: "" };
  if (INTERACTION_REQUIRED_STAGES.includes(status)) return { kind: "call", content: "" };
  return {};
}

export function isStatusExtraValid(status, value) {
  if (status === "lost") return !!value.loss_reason;
  if (status === "meeting_scheduled") return !!value.due_date;
  if (INTERACTION_REQUIRED_STAGES.includes(status)) return !!value.content?.trim();
  return true;
}

// Builds the extra payload merged into POST /prospects/{id}/status.
export function buildStatusExtraPayload(status, value) {
  if (status === "lost") return { loss_reason: value.loss_reason };
  if (status === "meeting_scheduled") {
    return { meeting: { due_date: value.due_date, description: value.description } };
  }
  if (INTERACTION_REQUIRED_STAGES.includes(status)) {
    return { interaction: { kind: value.kind, content: value.content } };
  }
  return {};
}

export default function StatusExtraFields({ status, value, onChange }) {
  if (status === "lost") {
    return (
      <div className="space-y-1.5">
        <Label className="text-xs">Motif de perte</Label>
        <Select value={value.loss_reason} onValueChange={(v) => onChange({ ...value, loss_reason: v })}>
          <SelectTrigger data-testid="select-loss-reason"><SelectValue /></SelectTrigger>
          <SelectContent>
            {LOSS_REASONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
    );
  }

  if (status === "meeting_scheduled") {
    return (
      <div className="space-y-4">
        <div className="space-y-1.5">
          <Label className="text-xs">Date du rendez-vous</Label>
          <Input
            type="date"
            data-testid="input-meeting-due"
            value={value.due_date}
            onChange={(e) => onChange({ ...value, due_date: e.target.value })}
          />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Détails (mode, lieu...)</Label>
          <Textarea
            data-testid="input-meeting-description"
            rows={3}
            placeholder="Visio, présentiel, adresse..."
            value={value.description}
            onChange={(e) => onChange({ ...value, description: e.target.value })}
          />
        </div>
      </div>
    );
  }

  if (INTERACTION_REQUIRED_STAGES.includes(status)) {
    return (
      <div className="space-y-4">
        <div className="space-y-1.5">
          <Label className="text-xs">Type de contact</Label>
          <Select value={value.kind} onValueChange={(v) => onChange({ ...value, kind: v })}>
            <SelectTrigger data-testid="select-interaction-kind"><SelectValue /></SelectTrigger>
            <SelectContent>
              {INTERACTION_TYPES.map((t) => <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Notes</Label>
          <Textarea
            data-testid="input-interaction-content"
            rows={3}
            placeholder="Ce qui s'est passé..."
            value={value.content}
            onChange={(e) => onChange({ ...value, content: e.target.value })}
          />
        </div>
      </div>
    );
  }

  return null;
}
